import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskType,
} from '@heygen/streaming-avatar';
import getConfig from '@base/lib/config';
import { useInterval } from '@vh/utility/TimerHooks';
import { VHPlayerEventBusContext } from '@vh/contexts/VHPlayerEventBus/VHPlayerEventBus.context';

const sessionTimeoutMS = 60 * 60 * 1000;
const intervalMS = 5000;

// Helper function to fetch access token
/** @type {() => Promise<string>} */
async function fetchAccessToken() {
  const apiKey = getConfig('REACT_APP_HEYGEN_API_KEY');

  const response = await fetch(
    'https://api.heygen.com/v1/streaming.create_token',
    {
      method: 'POST',
      headers: { 'x-api-key': apiKey },
    }
  );

  const { data } = await response.json();
  return data.token;
}

/**
 * @type {(videoElementRef: React.MutableRefObject<HTMLVideoElement | null>, options: {
 *      voiceId?: string;
 *      avatarName: string;
 *      onConnected?: () => void;
 *      onDisconnected?: () => void;
 *      onError?: (err: any) => void;
 *      onAvatarStartedTalking?: () => void;
 *      onAvatarFinishedTalking?: () => void;
 *      timeoutMS: number;
 *    }) => {
 *   interruptAvatar: () => void;
 *   connected: boolean;
 *   initializeAvatarSession: () => void;
 *   terminateAvatarSession: () => void;
 *   sendTextToAvatar: (text: string) => void;
 * }}
 * */
export const useHeyGen = (videoElementRef, options) => {
  const {
    voiceId,
    avatarName,
    onConnected,
    onDisconnected,
    onAvatarStartedTalking,
    onAvatarFinishedTalking,
    onError,
    timeoutMS,
  } = options;
  /** @type {React.Ref<StreamingAvatar | null>} */
  const avatarRef = useRef(null);
  const sessionDataRef = useRef(/** @type {React.Ref<any | null>} */ null);
  const [connected, setConnected] = useState(false);
  const timeoutRef = useRef(null);
  const sessionDurationRef = useRef(0);
  const connectedRef = useRef(connected);
  connectedRef.current = connected;

  // End the avatar session
  const terminateAvatarSession = useCallback(
    async (event) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      await avatarRef.current?.stopAvatar();
      setConnected(false);
      if (videoElementRef.current) {
        videoElementRef.current.srcObject = null;
      }
      avatarRef.current = null;
    },
    [videoElementRef]
  );

  useInterval(() => {
    if (connectedRef.current) {
      sessionDurationRef.current += intervalMS;
      if (sessionDurationRef.current >= sessionTimeoutMS) {
        console.log(
          'Terminating session due to breaching max limit of: ' +
            sessionTimeoutMS +
            'ms'
        );
        terminateAvatarSession();
      }
    }
  }, intervalMS);

  const resetTimer = useCallback(() => {
    let timeoutID = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      console.log('clear ', timeoutRef.current);
      timeoutRef.current = null;
    }
    timeoutRef.current = setTimeout(() => {
      console.log(
        'Timeout triggered: Terminating avatar session. timeoutID: ' + timeoutID
      );
      terminateAvatarSession();
    }, timeoutMS);
    timeoutID = timeoutRef.current;
    console.log('new timer', timeoutRef.current);
  }, [terminateAvatarSession, timeoutMS]);

  // Handle when avatar stream is ready
  const handleStreamReady = useCallback(
    (event) => {
      console.log('stream ready');
      console.log(event);
      sessionDurationRef.current = 0;
      resetTimer();
      if (event.detail && videoElementRef?.current) {
        console.log('set videoElementRef.current.srcObject');
        videoElementRef.current.srcObject = event.detail;
        videoElementRef.current.onloadedmetadata = () => {
          videoElementRef.current?.play().catch(console.error);
        };
        if (onConnected) {
          onConnected();
          setConnected(true);
        }
      } else {
        console.error('Stream is not available');
      }
    },
    [onConnected, videoElementRef, resetTimer]
  );

  // Handle stream disconnection
  const handleStreamDisconnected = useCallback(
    (event) => {
      console.log('Stream disconnected');
      if (videoElementRef?.current) {
        videoElementRef.current.srcObject = null;
      }
      if (onDisconnected) {
        onDisconnected();
      }
    },
    [onDisconnected, videoElementRef]
  );

  const handleAvatarFinishedTalking = useCallback(() => {
    console.log('finished talking');
    resetTimer();
    if (onAvatarFinishedTalking) {
      onAvatarFinishedTalking();
    }
  }, [resetTimer, onAvatarFinishedTalking]);

  const handleAvatarStartedTalking = useCallback(() => {
    if (onAvatarStartedTalking) {
      onAvatarStartedTalking();
    }
  }, [onAvatarStartedTalking]);

  const interruptAvatar = useCallback(() => {
    console.log('Interrupting avatar');
    // resetTimer()
    avatarRef.current?.interrupt();
  }, []);

  // Initialize streaming avatar session
  const initializeAvatarSession = useCallback(async () => {
    const token = await fetchAccessToken();
    const avatar = new StreamingAvatar({ token });

    console.log('Session data:', sessionDataRef.current);

    avatar.on(StreamingEvents.STREAM_READY, handleStreamReady);
    avatar.on(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
    avatar.on(StreamingEvents.AVATAR_START_TALKING, handleAvatarStartedTalking);
    avatar.on(StreamingEvents.AVATAR_STOP_TALKING, handleAvatarFinishedTalking);
    avatarRef.current = avatar;

    try {
      sessionDataRef.current = await avatar.createStartAvatar({
        quality: AvatarQuality.Medium,
        avatarName,
        disableIdleTimeout: true,
        voice: {
          voiceId,
        },
      });
    } catch (e) {
      if (onError) {
        onError(e);
      }
    }
  }, [
    avatarName,
    handleAvatarFinishedTalking,
    handleAvatarStartedTalking,
    handleStreamDisconnected,
    handleStreamReady,
    onError,
    voiceId,
  ]);

  /** @type {(text: string) => void} */
  const sendTextToAvatar = useCallback(async (text) => {
    console.log('SPEAK:', text);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    avatarRef.current?.speak({
      text,
      taskType: TaskType.REPEAT,
    });
  }, []);

  return {
    connected,
    initializeAvatarSession,
    terminateAvatarSession,
    sendTextToAvatar,
    interruptAvatar,
  };
};
